import { useEffect, useState } from "react";
import { Spin, Table, Button, Popconfirm, Tag, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";

import ContentCard from "../../../components/ContentCard/ContentCard";
import DefaultCardHeader from "../../../components/DefaultCardHeader/DefaultCardHeader";
import { REGION_LABELS } from "../regions";
import "./SponsorshipList.css";

const antIcon = <Spin size="large" />;

const emptyCell = <span style={{ color: "rgba(0, 0, 0, 0.35)" }}>N/A</span>;

const formatDate = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
};

export default function SponsorshipList() {
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [submissions, setSubmissions] = useState([]);

  const history = useHistory();

  const fetchAllSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/sponsor/all`
      );
      const data = await response.json();

      if (data.success) {
        setSubmissions(data.data);
      }
    } catch {
      // fetch error
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllSubmissions();
  }, []);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/sponsor/${id}`,
        { method: "DELETE" }
      );
      const data = await response.json();

      if (data.success) {
        message.success("Enquiry deleted");
        setSubmissions((prev) => prev.filter((item) => item.id !== id));
      } else {
        message.error(data.message || "Failed to delete enquiry");
      }
    } catch {
      message.error("Failed to delete enquiry");
    }
    setDeletingId(null);
  };

  const columns = [
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
    },
    {
      title: "Organization",
      dataIndex: "organizationName",
      key: "organizationName",
      render: (text) => text || emptyCell,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Projects",
      dataIndex: "projectCount",
      key: "projectCount",
      align: "center",
      sorter: (a, b) => a.projectCount - b.projectCount,
    },
    {
      title: "Estimated Total",
      dataIndex: "estimatedTotal",
      key: "estimatedTotal",
    },
    {
      title: "Where",
      dataIndex: "region",
      key: "region",
      // OTHER means the region still has to be scoped, so it is the one that
      // needs a person to look at it.
      render: (text) => (
        <Tag color={text === "NIGERIA" ? "green" : "orange"}>
          {REGION_LABELS[text] || text}
        </Tag>
      ),
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
      render: formatDate,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Popconfirm
          title="Delete this enquiry?"
          description="This action cannot be undone."
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={(e) => {
            e?.stopPropagation?.();
            handleDelete(record.id);
          }}
          onCancel={(e) => e?.stopPropagation?.()}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            loading={deletingId === record.id}
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <ContentCard
      isStickyHead
      headerContent={
        <div>
          <DefaultCardHeader>
            <div className="ambassadors-card__header">
              Sponsorship Enquiries
            </div>
          </DefaultCardHeader>
        </div>
      }
    >
      <div style={{ paddingTop: 4 }}>
        <Table
          className="sponsorship-submissions-table"
          rowKey="id"
          dataSource={submissions}
          columns={columns}
          scroll={{ x: "max-content" }}
          pagination={{
            defaultPageSize: 15,
            pageSizeOptions: ["15", "25", "50", "100"],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} enquiries`,
          }}
          loading={{
            spinning: loading,
            indicator: <Spin indicator={antIcon} />,
          }}
          locale={{ emptyText: "No sponsorship enquiries found" }}
          onRow={(record) => {
            return {
              onClick: () => history.push(`/sponsorships/${record.id}`),
            };
          }}
        />
      </div>
    </ContentCard>
  );
}
